import imageCompression from 'browser-image-compression';

/** Final max size after processing (PDF direct, image compressed). */
export const CMR_MAX_BYTES = 2 * 1024 * 1024;

/** Max image size accepted before client-side compression (CMR flow). */
export const CMR_IMAGE_MAX_INPUT_BYTES = 6 * 1024 * 1024;

/** Final max size for bank mismatch / supporting documents. */
export const SUPPORTING_DOC_MAX_BYTES = 2 * 1024 * 1024;

/** Max image input before compression for bank mismatch / supporting documents. */
export const SUPPORTING_DOC_IMAGE_MAX_INPUT_BYTES = 5 * 1024 * 1024;

const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

export function isImageFile(file) {
  if (!file) return false;
  if (file.type?.startsWith('image/')) return true;
  return /\.(jpe?g|png)$/i.test(file.name);
}

export function isPdfFile(file) {
  if (!file) return false;
  if (file.type === 'application/pdf') return true;
  return /\.pdf$/i.test(file.name);
}

export function isAllowedCmrFile(file) {
  if (!file) return false;
  if (ALLOWED_MIME_TYPES.includes(file.type)) return true;
  return /\.(pdf|jpe?g|png)$/i.test(file.name);
}

async function processDocumentForUpload(file, { maxOutputBytes, maxImageInputBytes, imageTooLargeError }) {
  if (!isAllowedCmrFile(file)) {
    return { ok: false, error: 'Please upload a PDF, JPG, or PNG file.' };
  }

  if (isPdfFile(file)) {
    if (file.size > maxOutputBytes) {
      return { ok: false, error: 'PDF must be under 2 MB.' };
    }
    return { ok: true, file };
  }

  if (isImageFile(file)) {
    if (file.size > maxImageInputBytes) {
      return { ok: false, error: imageTooLargeError };
    }

    if (file.size <= maxOutputBytes) {
      return { ok: true, file };
    }

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: maxOutputBytes / (1024 * 1024),
        maxWidthOrHeight: 2560,
        useWebWorker: true,
        initialQuality: 0.85,
        fileType: file.type || undefined,
      });

      if (compressed.size > maxOutputBytes) {
        return {
          ok: false,
          error: 'Could not compress image under 2 MB. Please use a smaller image.',
        };
      }

      const baseName = file.name.replace(/\.[^.]+$/, '') || 'document';
      const ext = compressed.type === 'image/png' ? 'png' : 'jpg';
      const finalFile = new File([compressed], `${baseName}.${ext}`, {
        type: compressed.type || 'image/jpeg',
        lastModified: Date.now(),
      });

      return { ok: true, file: finalFile, wasCompressed: true };
    } catch {
      return { ok: false, error: 'Failed to compress image. Please try another file.' };
    }
  }

  return { ok: false, error: 'Please upload a PDF, JPG, or PNG file.' };
}

/**
 * Validates and prepares a CMR file for upload.
 * - PDF: max 2 MB, no compression.
 * - Image: max 6 MB input, compressed to ≤ 2 MB when over 2 MB.
 */
export async function processCmrFileForUpload(file) {
  return processDocumentForUpload(file, {
    maxOutputBytes: CMR_MAX_BYTES,
    maxImageInputBytes: CMR_IMAGE_MAX_INPUT_BYTES,
    imageTooLargeError: 'Image must be under 6 MB.',
  });
}

/**
 * Validates and prepares bank mismatch / supporting documents for upload.
 * - PDF: max 2 MB, no compression.
 * - Image: max 5 MB input, compressed to ≤ 2 MB when over 2 MB.
 */
export async function processSupportingDocumentForUpload(file) {
  return processDocumentForUpload(file, {
    maxOutputBytes: SUPPORTING_DOC_MAX_BYTES,
    maxImageInputBytes: SUPPORTING_DOC_IMAGE_MAX_INPUT_BYTES,
    imageTooLargeError: 'Image must be under 5 MB.',
  });
}
